import * as React from 'react'
import { cn } from '@cocoonspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { InfoIcon, ThumbsUpIcon, TriangleAlertIcon } from 'lucide-react'

const alertVariants = cva(
	'relative flex w-full flex-row gap-3 rounded-lg px-4 py-3 text-sm shadow-xs',
	{
		defaultVariants: {
			variant: 'default',
		},
		variants: {
			variant: {
				default: 'border-card bg-card text-foreground',
				destructive:
					'border-destructive/50 bg-card text-destructive dark:border-destructive [&>svg]:text-destructive',
				warning:
					'border-warning/50 bg-card text-warning dark:border-warning [&>svg]:text-warning',
			},
		},
	},
)

const Alert = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
	<div
		className={cn(alertVariants({ variant }), className)}
		ref={ref}
		role='alert'
		{...props}
	/>
))

Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h5
		className={cn(
			'mt-0.5 mb-1 leading-none font-medium font-semibold tracking-tight',
			className,
		)}
		ref={ref}
		{...props}
	/>
))

AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<div
		className={cn('text-sm leading-none [&_p]:leading-relaxed', className)}
		ref={ref}
		{...props}
	/>
))

AlertDescription.displayName = 'AlertDescription'

const AlertIcon = ({ type }: { type?: 'bad' | 'good' | 'neutral' }) => {
	if (type === 'bad') return <TriangleAlertIcon className='shrink-0' />
	if (type === 'good') return <ThumbsUpIcon className='shrink-0' />
	return <InfoIcon className='shrink-0' />
}

export { Alert, AlertDescription, AlertIcon, AlertTitle }
